import { Step } from 'react-joyride';
import React, { useContext } from 'react';
import { Box, Button, alpha, Stack } from '@mui/material';
import { TourContext } from 'app/tour/index';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';

export default function TourToolTip({
    step,
    index,
    isLastStep,
    size,
}: {
    step: Step;
    index: number;
    isLastStep: boolean;
    size: number;
}) {
    const activeTour = useContext(TourContext);
    if (!activeTour) {
        return null;
    }
    const { isStepTransition } = activeTour.getTourStatus();
    const { t } = useBundleTranslation('app/tour');
    //@ts-ignore
    const tourName = activeTour.constructor?.tourName;
    return (
        <Box
            sx={{
                backgroundColor: 'background.default',
                padding: 1.5,
                borderRadius: '4px',
                position: 'relative',
                '& ~ .__floater__arrow': {
                    justifyContent: 'center',
                    svg: {
                        display: 'none',
                    },
                    '@keyframes highlight-target': {
                        '0%': { opacity: 0 },
                        '100%': { opacity: 1, transform: 'scale(1.5)' },
                    },
                    '&>span:before': {
                        content: "''",
                        width: '10px',
                        height: '10px',
                        backgroundColor: 'background.default',
                        transform: 'rotate(45deg)',
                        transformOrigin: 'center',
                        zIndex: -1,
                    },
                    '&>span:after': {
                        content: "''",
                        width: '24px',
                        height: '24px',
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        border: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.64)}`,
                        position: 'absolute',
                        borderRadius: '50%',
                        zIndex: -2,
                        animation: 'highlight-target 1s ease-out infinite alternate',
                    },
                },
                '.__floater[x-placement^="bottom"] & ~ .__floater__arrow>span:after': {
                    marginLeft: '-7px',
                    marginTop: '-21px',
                },
                '.__floater[x-placement^="right"] & ~ .__floater__arrow>span:after': {
                    marginLeft: '-22px',
                    marginTop: '-7px',
                },
                '.__floater[x-placement^="top"] & ~ .__floater__arrow>span:after': {
                    marginLeft: '-6px',
                    marginTop: '11px',
                },
                '.__floater[x-placement^="left"] & ~ .__floater__arrow>span:after': {
                    marginLeft: '11px',
                    marginTop: '-6px',
                },
                '.__floater[x-placement^="bottom"] & ~ .__floater__arrow>span:before': {
                    marginTop: '12px',
                },
                '.__floater[x-placement^="right"] & ~ .__floater__arrow>span:before': {
                    marginLeft: '12px',
                },
                '.__floater[x-placement^="top"] & ~ .__floater__arrow>span:before': {
                    marginTop: '-21px',
                },
                '.__floater[x-placement^="left"] & ~ .__floater__arrow>span:before': {
                    marginLeft: '-21px',
                },
            }}
        >
            {isStepTransition && (
                <LoadingPlaceholder
                    sx={{
                        position: 'absolute',
                        backgroundColor: (theme) => alpha(theme.palette.background.default, 0.5),
                        color: (theme) => alpha(theme.palette.text.primary, 0.4),
                    }}
                />
            )}

            <Stack direction={'row'} sx={{ pb: 1.5 }}>
                <Box flexGrow={1}>{t(`${tourName}.${step.content}`)}</Box>
                <Box
                    sx={{
                        flexShrink: 0,
                        ml: 2,
                        display: 'inline-flex',
                        fontSize: '16px',
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' },
                    }}
                    onClick={() => activeTour.endTour()}
                >
                    <IconMi icon={'times'} />
                </Box>
            </Stack>

            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Box sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.56) }}>
                    {t('__sys.step_ind', {
                        currentStep: index + 1,
                        amountStep: size,
                    })}
                </Box>
                <Box>
                    {index > 0 && (
                        <Button
                            variant="light"
                            onClick={() => {
                                activeTour.goToPreviousStep();
                            }}
                        >
                            {t('__sys.back')}
                        </Button>
                    )}
                    {isLastStep ? (
                        <Button
                            sx={{ ml: 1 }}
                            onClick={() => activeTour.endTour()}
                            color="success"
                            startIcon={<IconMi icon={'thumbs-up'} />}
                        >
                            {t('__sys.done')}
                        </Button>
                    ) : (
                        <Button sx={{ ml: 1 }} onClick={() => activeTour.goToNextStep()}>
                            {t('__sys.next')}
                        </Button>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
